import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { APP_RELEASE } from '../../../environments/release';
import { CORRELATION_HEADER, lastFailedCallId, newCorrelationId } from './correlation';
import { recordClientError } from './client-error-buffer';

/** At most this many reports per page load, and the same message only once a minute. */
const MAX_REPORTS = 10;
const REPEAT_WINDOW_MS = 60_000;

/**
 * OPS-02: uncaught browser errors go to the API (POST api/ClientErrors), which logs them with the API's own
 * errors, scrubbed the same way, and forwards them to the error tracker when one is configured. Failed HTTP calls
 * are not reported again: the API already logged them. The report bypasses the interceptors (no toast or
 * sign-out if it fails) and never throws.
 */
@Injectable()
export class ClientErrorHandler implements ErrorHandler {
  private readonly http = new HttpClient(inject(HttpBackend));
  private sent = 0;
  private readonly lastSent = new Map<string, number>();

  handleError(error: unknown): void {
    console.error(error);
    try {
      recordClientError(error);
      this.report(error);
    } catch {
      // reporting must never break the page
    }
  }

  private report(error: unknown, now = Date.now()): void {
    const original = (error as any)?.rejection ?? error; // unhandled promise rejections are wrapped
    if (original instanceof HttpErrorResponse) return;

    const message = String((original as any)?.message ?? original ?? '').slice(0, 1000);
    if (!message || this.sent >= MAX_REPORTS) return;
    if (now - (this.lastSent.get(message) ?? -Infinity) < REPEAT_WINDOW_MS) return;
    this.lastSent.set(message, now);
    this.sent++;

    let headers = new HttpHeaders({ [CORRELATION_HEADER]: newCorrelationId() });
    const token = sessionStorage.getItem('token');
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    this.http.post(environment.apiUrl + 'ClientErrors', {
      message,
      stack: String((original as any)?.stack ?? '').slice(0, 4000),
      page: location.pathname,
      lastCorrelationId: lastFailedCallId(now),
      release: APP_RELEASE,
    }, { headers }).subscribe({ error: () => undefined });
  }
}
